// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// Janelas flutuantes — arrastáveis, e que não tapam o mapa
// ============================================================================
//
// PORQUE EXISTEM
//   Retorno do cliente (2026-08-05): *«relativamente à aba de pedidos, não quero que a
//   posiciones aí, quero que seja meio que um pop-up que permita ver o mapa enquanto
//   selecciona a opção que deseja»*.
//
//   Tem razão, e a razão é operacional: **escolher que autocarro mobilizar é uma decisão
//   que se toma a olhar para o mapa.** A lista era um menu preso à barra, e a janela das
//   dez opções escurecia o ecrã inteiro — as duas trabalhavam contra o gesto que existem
//   para servir.
//
// ⚠️ O QUE ISTO NÃO É
//   Não é um gestor de janelas. Não há minimizar, empilhar, encaixar nem lembrar
//   posições entre sessões. É o mínimo que responde ao pedido: uma caixa que se agarra
//   pelo cabeçalho, se arrasta, e **nunca escurece nem cobre o ecrã todo**.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';

export interface Posicao {
  readonly x: number;
  readonly y: number;
}

/**
 * Mantém a janela dentro do ecrã.
 *
 * ⚠️ **Deixa sempre uma margem visível do cabeçalho**, e é o ponto todo: uma janela
 * arrastada para fora perde-se e não há como a recuperar sem recarregar a página. O
 * limite é sobre o **canto superior esquerdo**, porque é por aí que ela se agarra.
 *
 * Função pura de propósito — é a parte que se testa sem DOM, e é a que erra em silêncio.
 */
export function limitarAoEcra(
  p: Posicao,
  janela: { readonly largura: number; readonly altura: number },
  ecra: { readonly largura: number; readonly altura: number },
  margem = 48,
): Posicao {
  // À esquerda e em cima pode encostar; para a direita e para baixo tem de sobrar
  // `margem` do cabeçalho, senão não há por onde a trazer de volta.
  const maxX = Math.max(0, ecra.largura - margem);
  const maxY = Math.max(0, ecra.altura - margem);
  return {
    x: Math.min(Math.max(p.x, -(janela.largura - margem)), maxX),
    y: Math.min(Math.max(p.y, 0), maxY),
  };
}

/**
 * Posição inicial ancorada a um canto, em coordenadas absolutas.
 *
 * Recebe o tamanho do ecrã em vez de o ler, para ser testável e para o cálculo ficar
 * num só sítio.
 *
 * ⚠️⚠️ **`margem` e `topo` juntos são a CASCATA, e sem eles duas janelas do mesmo lado
 * nascem EXACTAMENTE sobrepostas.** Nenhum dos chamadores é exclusivo do outro — `Gestão`,
 * `Configuração`, `Contas` e as restantes janelas de topo abrem-se por botões
 * independentes, e nada as fecha quando outra abre (2026-09-02: «as informações aparecem
 * todas em cima das outras»). Como todas usavam a omissão (`margem = 16, topo = 64`),
 * duas do MESMO `canto` caíam no mesmo pixel — um planeador que abrisse `Configuração` a
 * seguir a `Contas` via só a segunda, com a primeira escondida por baixo dela e sem
 * nenhum sinal de que lá estava.
 *
 * ⚠️ **A primeira correcção só variava `topo`, e não chegou** — 📗 cliente, 2026-09-02,
 * ainda com duas janelas quase totalmente sobrepostas: com janelas largas (500–760 px)
 * um desvio vertical de 32 px é impercetível. 📌 Cada janela de topo passa agora um par
 * `margem`/`topo` PRÓPRIO, em degraus de 48 px a partir de (16, 64) — ver a lista de
 * valores no cabeçalho de cada chamada. Como `margem` desloca a janela na HORIZONTAL
 * (afasta-a do canto) e `topo` na VERTICAL, a cascata é DIAGONAL — visível mesmo quando
 * as janelas são largas. É uma cascata ESTÁTICA e não um gestor de janelas (a nota no
 * topo do ficheiro continua verdadeira): não conta quantas estão abertas, só garante que
 * duas janelas quaisquer do mesmo lado nunca partilham o canto de nascença. Quem quiser
 * vê-las alinhadas arrasta-as — é o que o punho é para.
 */
export function cantoInicial(
  canto: 'direita' | 'esquerda',
  janela: { readonly largura: number; readonly altura: number },
  ecra: { readonly largura: number; readonly altura: number },
  margem = 16,
  topo = 64,
): Posicao {
  const x = canto === 'direita' ? ecra.largura - janela.largura - margem : margem;
  return limitarAoEcra({ x: Math.max(margem, x), y: topo }, janela, ecra);
}

/**
 * Arrastar pelo cabeçalho.
 *
 * ⚠️ **Os ouvintes de movimento estão no `window`, não no cabeçalho.** É o que faz o
 * arrasto sobreviver a o cursor sair da janela — arrastar depressa deixa o cursor à
 * frente do elemento, e um ouvinte no punho perdia o gesto a meio.
 *
 * ⚠️ **E é por isso que NÃO há `setPointerCapture`.** Escrevi-o na primeira versão, com
 * um comentário a dizer que era ele que resolvia isto. Era redundante — o `window` já
 * resolve — e o `jsdom` não o implementa, logo teria feito o arrasto explodir em teste
 * por uma razão que não é a que se estava a verificar.
 *
 * ⚠️ **A posição NÃO persiste ao fechar.** É escolha declarada: a janela volta ao canto
 * de omissão, que está escolhido para não tapar o mapa. Persistir exigia levantar o
 * estado acima do componente e não é o que o pedido pede.
 */
export function useArrastavel(
  inicial: () => Posicao,
): {
  readonly posicao: Posicao;
  readonly aoPressionar: (e: React.PointerEvent) => void;
  readonly aArrastar: boolean;
} {
  const [posicao, definirPosicao] = useState<Posicao>(inicial);
  const [aArrastar, definirAArrastar] = useState(false);
  // O deslocamento entre o cursor e o canto da janela, para ela não «saltar» para
  // debaixo do cursor no primeiro pixel de arrasto.
  const pega = useRef<{ dx: number; dy: number; largura: number; altura: number } | null>(null);

  // Se o ecrã encolher — janela redimensionada, ou monitor desligado — uma janela que
  // estava à direita fica fora. Volta a entrar.
  useEffect(() => {
    const aoRedimensionar = (): void => {
      definirPosicao((p) =>
        limitarAoEcra(
          p,
          { largura: pega.current?.largura ?? 560, altura: pega.current?.altura ?? 400 },
          { largura: window.innerWidth, altura: window.innerHeight },
        ),
      );
    };
    window.addEventListener('resize', aoRedimensionar);
    return () => window.removeEventListener('resize', aoRedimensionar);
  }, []);

  const aoPressionar = useCallback(
    (e: React.PointerEvent) => {
      // Só o botão principal, e não sobre um botão dentro do cabeçalho — senão fechar a
      // janela começava um arrasto.
      if (e.button !== 0) return;
      if ((e.target as HTMLElement).closest('button') !== null) return;
      const caixa = (e.currentTarget as HTMLElement).parentElement;
      // ⚠️ O canto vem do ESTADO e não de `getBoundingClientRect`. O estado é que manda
      // no `style`; ler a posição de volta do DOM criava uma segunda fonte de verdade a
      // competir com ele — e as duas divergem no primeiro quadro em que o React ainda
      // não pintou. Do DOM vem só o TAMANHO, que o estado não conhece.
      pega.current = {
        dx: e.clientX - posicao.x,
        dy: e.clientY - posicao.y,
        largura: caixa?.offsetWidth ?? 560,
        altura: caixa?.offsetHeight ?? 400,
      };
      definirAArrastar(true);
    },
    [posicao.x, posicao.y],
  );

  useEffect(() => {
    if (!aArrastar) return;
    const mover = (e: PointerEvent): void => {
      const g = pega.current;
      if (g === null) return;
      definirPosicao(
        limitarAoEcra(
          { x: e.clientX - g.dx, y: e.clientY - g.dy },
          { largura: g.largura, altura: g.altura },
          { largura: window.innerWidth, altura: window.innerHeight },
        ),
      );
    };
    const largar = (): void => definirAArrastar(false);
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', largar);
    window.addEventListener('pointercancel', largar);
    return () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', largar);
      window.removeEventListener('pointercancel', largar);
    };
  }, [aArrastar]);

  return { posicao, aoPressionar, aArrastar };
}

/**
 * 🔴 2026-10-26 · TODA A JANELA QUE ABRE ARRASTA-SE — as que nascem ao CENTRO também.
 *
 * 📗 Cliente: *«todas as abas que abrem têm de poder ser arrastadas, actualmente algumas não o
 * são»*. Medido: **23** janelas sem `useArrastavel` — as caixas curtas `.janela--motivo`, a edição
 * do dia, a impressão, a caixa de entrada e a validação da proposta. Tapavam o mapa no sítio onde
 * nasciam, e o que se decide nelas decide-se a olhar para o mapa.
 *
 * 📌 **A mesma mecânica de `useArrastavel`, e não uma segunda:** só muda onde nasce — centrada na
 * horizontal, a 12 % do topo. Devolve o `estilo` da janela e as propriedades do cabeçalho (que é
 * por onde se agarra; os botões dele continuam a ser botões). Guardado por
 * `todas-as-janelas-arrastam.test.ts`, que recusa a próxima janela sem nenhum dos dois hooks.
 */
export function useJanelaAoCentro(largura: number): {
  readonly estilo: { readonly left: number; readonly top: number };
  readonly cab: {
    readonly onPointerDown: (e: React.PointerEvent) => void;
    readonly 'data-agarrada': 'sim' | undefined;
  };
} {
  const { posicao, aoPressionar, aArrastar } = useArrastavel(() =>
    limitarAoEcra(
      { x: Math.max(16, (window.innerWidth - largura) / 2), y: Math.round(window.innerHeight * 0.12) },
      { largura, altura: 360 },
      { largura: window.innerWidth, altura: window.innerHeight },
    ),
  );
  return {
    estilo: { left: posicao.x, top: posicao.y },
    cab: { onPointerDown: aoPressionar, 'data-agarrada': aArrastar ? 'sim' : undefined },
  };
}

/**
 * As janelas abertas que fecham com `Escape`, pela ordem em que abriram — a de CIMA é a última.
 *
 * 🔴 2026-10-22 · Achado da revisão de 2026-10-21: cada janela punha o seu ouvinte na `window`, logo
 * UM `Escape` fechava TODAS as janelas abertas de uma vez (38 usam este hook) — quem fechava a caixa
 * de confirmação perdia a lista de pedidos, o detalhe e as consultas que tinha abertos por baixo.
 * 📌 Agora há UM ouvinte, e cada `Escape` fecha só a janela de cima; o seguinte fecha a de baixo.
 */
const pilhaDoEscape: { readonly fechar: { current: () => void } }[] = [];

function aoTeclarEscape(e: KeyboardEvent): void {
  if (e.key !== 'Escape') return;
  pilhaDoEscape[pilhaDoEscape.length - 1]?.fechar.current();
}

/** `Escape` fecha — só a janela de cima. O gesto que todos esperam de uma janela. */
export function useEscape(
  fechar: () => void,
  /**
   * Para o que está sempre montado e só às vezes aberto (um diálogo que aparece por cima de tudo):
   * entra na pilha quando abre — e por isso fica no TOPO — e sai quando fecha.
   */
  activo = true,
): void {
  // ⚠️ Por referência: a janela entra na pilha UMA vez (ao abrir) e não volta ao topo a cada render
  //    só porque o `fechar` mudou de identidade.
  const ref = useRef(fechar);
  ref.current = fechar;
  useEffect(() => {
    if (!activo) return undefined;
    const entrada = { fechar: ref };
    if (pilhaDoEscape.length === 0) window.addEventListener('keydown', aoTeclarEscape);
    pilhaDoEscape.push(entrada);
    return () => {
      const i = pilhaDoEscape.indexOf(entrada);
      if (i >= 0) pilhaDoEscape.splice(i, 1);
      if (pilhaDoEscape.length === 0) window.removeEventListener('keydown', aoTeclarEscape);
    };
  }, [activo]);
}
